// The Duplicates panel on the Patients page: the practice's candidate duplicate
// patients in three tiers (exact, probable, skipped with a reason), a Merge button
// per group and "Merge all exact matches", both behind a confirm modal.
//
// Three rules the panel keeps for the server, not instead of it:
//
// 1. NOTHING MERGES WITHOUT A CONFIRM. Each merge posts `{patient_ids, confirm: true}`
//    only after the modal's Merge button; "Merge all" first posts a dry run and shows
//    its counts in the modal before the real call.
// 2. A REFUSAL IS THE SERVER'S SENTENCE. 400/403/404/409 are shown verbatim. A 503
//    (the audit table is not migrated yet) is shown as a standing notice and every
//    merge control is disabled until a reload answers otherwise.
// 3. THE SERVER PICKS THE SURVIVOR. `survivor_id` is shown as "Will keep"; the panel
//    never chooses or sends one.
//
// Mounted by PatientsPage for staff only (`!readOnly`); the Merge buttons also need
// `isAdmin`, mirroring the routes (list = biller, merge = admin). No phone number or
// email address reaches the DOM: the projection carries `has_mobile` / `has_email`
// and the cards show yes/no chips.

import React, { useCallback, useEffect, useState } from "react";

import { fetchDuplicates, mergeExact, mergePatients } from "./api";
import { formatPatientName } from "../../utils/formatName";
import "./DuplicatesPanel.css";

const WHY_LABEL = {
  first_name_prefix: "Same last name and DOB; first names differ by a prefix or initial",
  phone: "Same phone number",
  email: "Same email and last name",
};

function whyLabel(why) {
  return WHY_LABEL[why] || why || "";
}

function groupIds(group) {
  return (group.patients || []).map((p) => p.id);
}

function groupKey(group) {
  return groupIds(group).join("-");
}

// {claims: 3, coverages: 1} → "claims 3 · coverages 1"
function formatMoved(moved) {
  const pairs = Object.entries(moved || {}).filter(([, n]) => Number(n) > 0);
  if (!pairs.length) return "nothing to move";
  return pairs.map(([table, n]) => `${table.replace(/_/g, " ")} ${n}`).join(" · ");
}

function Message({ tone, children, onClose, testId }) {
  return (
    <div
      className={`pt-message ${tone}`}
      role={tone === "error" ? "alert" : undefined}
      data-testid={testId}
    >
      <span>{children}</span>
      {onClose && (
        <button
          type="button"
          className="pt-message-close"
          aria-label="Dismiss"
          onClick={onClose}
        >
          &times;
        </button>
      )}
    </div>
  );
}

function Chip({ tone = "muted", children }) {
  return <span className={`pt-chip pt-chip-${tone}`}>{children}</span>;
}

function PatientCell({ patient, survivor }) {
  return (
    <div
      className={`dup-patient${survivor ? " is-survivor" : ""}`}
      data-testid="dup-patient"
      data-patient-id={patient.id}
    >
      <div className="dup-patient-head">
        <span className="dup-patient-name">{formatPatientName(patient.display_name)}</span>
        {survivor && (
          <span className="pt-status pt-status-good" data-testid="dup-will-keep">
            Will keep
          </span>
        )}
      </div>
      <dl className="dup-patient-facts">
        <div>
          <dt>DOB</dt>
          <dd>{patient.dob || "—"}</dd>
        </div>
        <div>
          <dt>MRN / PM id</dt>
          <dd>{patient.pm_id || "—"}</dd>
        </div>
        <div>
          <dt>EMR id</dt>
          <dd>{patient.emr_id || "—"}</dd>
        </div>
        <div>
          <dt>Claims</dt>
          <dd>{Number(patient.claims) || 0}</dd>
        </div>
        <div>
          <dt>Appointments</dt>
          <dd>{Number(patient.appointments) || 0}</dd>
        </div>
      </dl>
      <div className="dup-patient-chips">
        <Chip tone={patient.has_mobile ? "good" : "muted"}>
          {patient.has_mobile ? "Mobile on file" : "No mobile"}
        </Chip>
        <Chip tone={patient.has_email ? "good" : "muted"}>
          {patient.has_email ? "Email on file" : "No email"}
        </Chip>
        {patient.opted_out && <Chip tone="bad">Opted out</Chip>}
      </div>
    </div>
  );
}

function Group({ group, tier, canMerge, busy, onMerge }) {
  const patients = group.patients || [];
  const note = tier === "probable" ? whyLabel(group.why) : group.sentence || group.reason;
  return (
    <li className="dup-group" data-testid={`dup-group-${tier}`}>
      <div className="dup-group-row">
        {patients.map((patient) => (
          <PatientCell
            key={patient.id}
            patient={patient}
            survivor={group.survivor_id != null && patient.id === group.survivor_id}
          />
        ))}
      </div>
      <div className="dup-group-foot">
        <span className="dup-group-note">{note}</span>
        {canMerge && (
          <button
            type="button"
            className="pt-btn pt-btn-primary"
            data-testid="dup-merge"
            disabled={busy}
            onClick={() => onMerge(group)}
          >
            Merge
          </button>
        )}
      </div>
    </li>
  );
}

function Section({ title, count, children, testId }) {
  return (
    <section className="pt-card dup-section" data-testid={testId}>
      <div className="pt-card-head">
        <h3>
          {title} <span className="dup-count">({count})</span>
        </h3>
      </div>
      {children}
    </section>
  );
}

// The confirm modal, in the PostHoldConfirm pattern: overlay, dialog, Cancel and
// one primary action that is busy while the request runs.
function ConfirmModal({ title, lead, children, confirmLabel, busy, onConfirm, onCancel, canConfirm = true }) {
  return (
    <div className="modal-overlay" onClick={() => !busy && onCancel()}>
      <div
        className="modal-content dup-confirm"
        role="dialog"
        aria-labelledby="dup-confirm-title"
        data-testid="dup-confirm"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dup-confirm-head">
          <h3 id="dup-confirm-title">{title}</h3>
          <button
            type="button"
            className="pt-message-close"
            aria-label="Close"
            onClick={onCancel}
            disabled={busy}
          >
            &times;
          </button>
        </div>
        <p className="dup-confirm-lead">{lead}</p>
        {children}
        <div className="dup-confirm-footer">
          <button type="button" className="pt-btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="pt-btn pt-btn-primary"
            data-testid="dup-confirm-ok"
            onClick={onConfirm}
            disabled={busy || !canConfirm}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DuplicatesPanel({ practiceId, isAdmin, onMerged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // The 503 sentence; while set, every merge control is disabled.
  const [unavailable, setUnavailable] = useState(null);
  const [message, setMessage] = useState(null);
  // {kind: "group", group} | {kind: "exact", preview: result|null, previewError}
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);

  const load = useCallback(() => {
    if (!practiceId) return;
    setLoading(true);
    fetchDuplicates(practiceId)
      .then((payload) => {
        setData(payload);
        setUnavailable(null);
      })
      .catch((error) => {
        if (error.status === 503) {
          setUnavailable(error.message);
          setData((prev) => prev || { exact: [], probable: [], skipped: [], counts: {} });
        } else {
          setMessage({ type: "error", text: error.message });
        }
      })
      .finally(() => setLoading(false));
  }, [practiceId]);

  useEffect(() => {
    setData(null);
    setUnavailable(null);
    setMessage(null);
    setConfirm(null);
    load();
  }, [load]);

  const exact = (data && data.exact) || [];
  const probable = (data && data.probable) || [];
  const skipped = (data && data.skipped) || [];
  const counts = (data && data.counts) || {};
  const canMerge = !!isAdmin && !unavailable;

  const handleError = (error) => {
    if (error.status === 503) {
      setUnavailable(error.message);
    } else {
      setMessage({ type: "error", text: error.message });
    }
    // A 404/409 (already merged elsewhere) means the list on screen is stale.
    if (error.status === 404 || error.status === 409) load();
  };

  const removeGroup = (group) => {
    const key = groupKey(group);
    setData((prev) =>
      prev
        ? {
            ...prev,
            exact: prev.exact.filter((g) => groupKey(g) !== key),
            probable: (prev.probable || []).filter((g) => groupKey(g) !== key),
          }
        : prev,
    );
  };

  const runMerge = (group) => {
    setBusy(true);
    setMessage(null);
    setProgress("Merging…");
    mergePatients(practiceId, groupIds(group))
      .then((result) => {
        setConfirm(null);
        removeGroup(group);
        const n = (result.merged_ids || []).length;
        const filled = (result.filled || []).length;
        setMessage({
          type: "success",
          text:
            `Merged ${n} record${n === 1 ? "" : "s"} into #${result.survivor_id}. ` +
            `Moved: ${formatMoved(result.moved)}.` +
            (filled ? ` Filled ${filled} blank field${filled === 1 ? "" : "s"}.` : ""),
        });
        if (onMerged) onMerged(result);
        load();
      })
      .catch(handleError)
      .finally(() => {
        setBusy(false);
        setProgress(null);
      });
  };

  // "Merge all exact matches": open the modal, then fill it with the dry run.
  const openMergeAll = () => {
    setMessage(null);
    setConfirm({ kind: "exact", preview: null, previewError: null });
    setBusy(true);
    mergeExact(practiceId, { dryRun: true })
      .then((preview) => setConfirm((c) => (c && c.kind === "exact" ? { ...c, preview } : c)))
      .catch((error) => {
        if (error.status === 503) {
          setUnavailable(error.message);
          setConfirm(null);
        } else {
          setConfirm((c) => (c && c.kind === "exact" ? { ...c, previewError: error.message } : c));
        }
      })
      .finally(() => setBusy(false));
  };

  const runMergeAll = () => {
    setBusy(true);
    setMessage(null);
    setProgress("Merging all exact matches…");
    mergeExact(practiceId, { dryRun: false })
      .then((result) => {
        setConfirm(null);
        const skippedList = result.skipped || [];
        const text =
          `Merged ${result.groups || 0} group${result.groups === 1 ? "" : "s"} ` +
          `(${result.merged_rows || 0} record${result.merged_rows === 1 ? "" : "s"} folded in).` +
          (skippedList.length
            ? ` Skipped ${skippedList.length}: ${skippedList
                .map((s) => s.sentence || s.reason)
                .filter(Boolean)
                .join("; ")}.`
            : "");
        setMessage({ type: "success", text });
        if (onMerged) onMerged(result);
        load();
      })
      .catch(handleError)
      .finally(() => {
        setBusy(false);
        setProgress(null);
      });
  };

  return (
    <div className="dup-panel" data-testid="duplicates-panel">
      <div className="dup-head">
        <div>
          <h2>Duplicate patients</h2>
          <p className="pt-empty">
            {loading && !data
              ? "Looking for duplicates…"
              : `${Number(counts.rows || 0).toLocaleString()} patient rows · ` +
                `${exact.length} exact · ${probable.length} probable · ${skipped.length} skipped`}
          </p>
        </div>
        <div className="dup-head-actions">
          <button type="button" className="pt-btn" onClick={load} disabled={loading || busy}>
            {loading ? "Loading…" : "Reload"}
          </button>
          {isAdmin && (
            <button
              type="button"
              className="pt-btn pt-btn-primary"
              data-testid="dup-merge-all"
              onClick={openMergeAll}
              disabled={!canMerge || busy || loading || exact.length === 0}
              title={
                unavailable
                  ? unavailable
                  : exact.length === 0
                    ? "No exact matches to merge"
                    : "Merge every exact match, after a dry run"
              }
            >
              Merge all exact matches
            </button>
          )}
        </div>
      </div>

      {unavailable && (
        <Message tone="error" testId="dup-unavailable">
          {unavailable}
        </Message>
      )}
      {message && (
        <Message tone={message.type} onClose={() => setMessage(null)} testId="dup-notice">
          {message.text}
        </Message>
      )}
      {progress && (
        <p className="pt-note" data-testid="dup-progress">
          {progress}
        </p>
      )}

      {data && (
        <>
          <Section title="Exact matches" count={exact.length} testId="dup-section-exact">
            {exact.length === 0 ? (
              <p className="pt-empty">No exact matches.</p>
            ) : (
              <ul className="dup-groups">
                {exact.map((group) => (
                  <Group
                    key={groupKey(group)}
                    group={group}
                    tier="exact"
                    canMerge={canMerge}
                    busy={busy}
                    onMerge={(g) => setConfirm({ kind: "group", group: g })}
                  />
                ))}
              </ul>
            )}
          </Section>

          <Section title="Probable matches" count={probable.length} testId="dup-section-probable">
            <p className="pt-note dup-section-note">
              Never merged automatically. Check both records before merging.
            </p>
            {probable.length === 0 ? (
              <p className="pt-empty">No probable matches.</p>
            ) : (
              <ul className="dup-groups">
                {probable.map((group) => (
                  <Group
                    key={groupKey(group)}
                    group={group}
                    tier="probable"
                    canMerge={canMerge}
                    busy={busy}
                    onMerge={(g) => setConfirm({ kind: "group", group: g })}
                  />
                ))}
              </ul>
            )}
          </Section>

          <Section title="Skipped" count={skipped.length} testId="dup-section-skipped">
            {skipped.length === 0 ? (
              <p className="pt-empty">Nothing skipped.</p>
            ) : (
              <ul className="dup-skipped">
                {skipped.map((item, i) => (
                  <li key={`${(item.patient_ids || []).join("-")}-${i}`} data-testid="dup-skipped-item">
                    <span className="dup-skipped-ids">
                      #{(item.patient_ids || []).join(", #")}
                    </span>
                    <span className="dup-skipped-reason">{item.sentence || item.reason}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </>
      )}

      {confirm && confirm.kind === "group" && (
        <ConfirmModal
          title="Merge these patients?"
          lead={
            `${(confirm.group.patients || []).length} records become one. Claims, coverages, ` +
            "appointments, eligibility, outreach and payments move to the record marked " +
            "“Will keep”; the other records are deleted. This cannot be undone."
          }
          confirmLabel={busy ? "Merging…" : "Merge"}
          busy={busy}
          onConfirm={() => runMerge(confirm.group)}
          onCancel={() => !busy && setConfirm(null)}
        >
          <ul className="dup-confirm-list">
            {(confirm.group.patients || []).map((p) => (
              <li key={p.id}>
                {formatPatientName(p.display_name)} · DOB {p.dob || "—"} · MRN {p.pm_id || "—"}
                {confirm.group.survivor_id === p.id ? " · will keep" : ""}
              </li>
            ))}
          </ul>
          {confirm.group.survivor_id == null && (
            <p className="pt-note">
              Atlas keeps the record with the MRN / PM id, then the EMR id, then the most claims.
            </p>
          )}
        </ConfirmModal>
      )}

      {confirm && confirm.kind === "exact" && (
        <ConfirmModal
          title="Merge all exact matches?"
          lead="Every exact group is merged into its survivor. Probable matches are not touched. This cannot be undone."
          confirmLabel={progress ? "Merging…" : "Merge all"}
          busy={busy}
          canConfirm={!!confirm.preview}
          onConfirm={runMergeAll}
          onCancel={() => !busy && setConfirm(null)}
        >
          {!confirm.preview && !confirm.previewError && (
            <p className="pt-note" data-testid="dup-dry-run-pending">
              Counting…
            </p>
          )}
          {confirm.previewError && (
            <Message tone="error">{confirm.previewError}</Message>
          )}
          {confirm.preview && (
            <div data-testid="dup-dry-run">
              <p className="dup-confirm-counts">
                <strong>{confirm.preview.groups || 0}</strong> group
                {confirm.preview.groups === 1 ? "" : "s"},{" "}
                <strong>{confirm.preview.merged_rows || 0}</strong> record
                {confirm.preview.merged_rows === 1 ? "" : "s"} folded in
                {(confirm.preview.skipped || []).length
                  ? `, ${confirm.preview.skipped.length} skipped`
                  : ""}
                .
              </p>
              {(confirm.preview.skipped || []).length > 0 && (
                <ul className="dup-confirm-list">
                  {confirm.preview.skipped.map((s, i) => (
                    <li key={i}>
                      #{(s.patient_ids || s.ids || []).join(", #")}: {s.sentence || s.reason}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </ConfirmModal>
      )}
    </div>
  );
}
